import { VertesiaClient } from "@vertesia/client"
import { ExecutionEnvironmentRef, ExecutionRun, Interaction, InteractionExecutionConfiguration, mergePromptsSchema, PopulatedInteraction } from "@vertesia/common"
import { JSONSchema4 } from "json-schema"

import { createCompositeStateProvider, Property } from "@hooks/use-composite-state"

import { ManagedObject } from "@plugins/form/ManagedObject"

export enum FormExecutionStatus {
    initial,
    pending,
    streaming,
    done,
    error,
}

export default class FormState {
    payload: ManagedObject
    config: InteractionExecutionConfiguration | undefined
    env: ExecutionEnvironmentRef | undefined

    executionRun: ExecutionRun | undefined
    error: Error | undefined

    executionStatus = new Property<FormExecutionStatus>(FormExecutionStatus.initial)
    streamingText = new Property<string>()

    constructor(
        public client: VertesiaClient,
        public interaction: Interaction,
        public result?: string
    ) {
        const schema: JSONSchema4 = mergePromptsSchema(interaction as PopulatedInteraction) || {}
        this.payload = new ManagedObject(schema, interaction.test_data || {})
        if (result) {
            this.streamingText.value = result
            this.executionStatus.value = FormExecutionStatus.done
        }
    }

    setStreamingText(text: string) {
        this.streamingText.value = text
    }

    get status() {
        return this.executionStatus.value
    }

    setStatus(status: FormExecutionStatus) {
        this.executionStatus.value = status
    }

    async run() {
        const config = this.config
        if (!config) {
            return Promise.reject(new ConfigValidationError("Run configuration not defined"))
        }
        if (!this.config?.model && !this.env?.provider.startsWith("virtual_")) {
            return Promise.reject(new ConfigValidationError("Model was not defined"))
        }

        this.error = undefined
        this.executionRun = undefined
        this.executionStatus.value = FormExecutionStatus.pending
        try {
            const uuid = crypto.randomUUID()
            const result = await this.client.runs.create({
                interaction: this.interaction.id,
                data: this.payload.value,
                config,
                tags: ["service", `service:${uuid}`, generateTaskName(this.interaction.name)],
                stream: true,
            })
            if (!window.location.hash) {
                window.location.hash = uuid
            }
            this.executionRun = result as ExecutionRun
            this.executionStatus.value = FormExecutionStatus.streaming
            return result
        } catch (err) {
            if (!(err instanceof Error)) {
                const error = err as any
                err = new Error(error.message ? error.message : String(error))
            }
            this.error = err as Error
            this.executionStatus.value = FormExecutionStatus.error
            throw err
        }
    }

    static readonly Provider = createCompositeStateProvider(FormState)
}

function generateTaskName(input: string | Interaction) {
    const date = new Date()

    const result = typeof input === "object" ? `${input.name} - ${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : createTaskName(input)
    return `taskName: ${result}`
}

function createTaskName(input: string, maxLength = 50) {
    if (!input) {
        return ""
    }

    let trimmed = input.trim()

    const firstLine = trimmed.split("\n")[0]

    const questionIndex = firstLine.indexOf("?")
    const exclamationIndex = firstLine.indexOf("!")

    let stopIndex = -1
    if (questionIndex !== -1 && exclamationIndex !== -1) {
        stopIndex = Math.min(questionIndex, exclamationIndex)
    } else if (questionIndex !== -1) {
        stopIndex = questionIndex
    } else if (exclamationIndex !== -1) {
        stopIndex = exclamationIndex
    }

    trimmed = stopIndex !== -1 ? firstLine.substring(0, stopIndex + 1) : firstLine

    if (trimmed.length <= maxLength) {
        return trimmed
    }

    const truncated = trimmed.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(" ")

    return lastSpaceIndex === -1 ? truncated : truncated.substring(0, lastSpaceIndex)
}

export class ConfigValidationError extends Error {}
